import { memo, useCallback } from 'react';
import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import { getBlockDriversData, getSelectedDriver, setDriver } from 'redux/PlanPageSlice';
import { resetDimension } from 'redux/DimensionPageSlice';
import { resetIndicator } from 'redux/IndicatorPageSlice';
import { resetItem, resetTableOrgChart } from 'redux/ItemPageSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { refetchPlanPageAfterInputPanelClose } from '../planPageFetchHelpers';
import { PLAN_INPUT_PANEL_WIDTH } from '../planInputPanelTheme';
import {
  PLAN_ACCENT_BLUE,
  PLAN_KPI_SELECTED_BG,
  PLAN_KPI_SELECTED_BORDER,
  PLAN_REVAMP_RADIUS,
  PLAN_TEXT,
} from '../planPageRevampUi';

import type { PlanPageRefetchRequest } from '../planPageFetchHelpers';

const getDriverName = (driver: any): string => {
  if (driver?.type === 'indicator') return driver?.name ?? '';
  if (driver?.type === 'dimension') return driver?.dim_name ?? '';
  return driver?.property_name ?? '';
};

const getDriverKey = (driver: any, index: number): string => {
  if (driver?.type === 'indicator') return `indicator-${driver?.id ?? index}`;
  if (driver?.type === 'dimension') return `dimension-${driver?.dim_id ?? index}`;
  return `property-${driver?.property_id ?? index}`;
};

const isSameDriver = (driver: any, selectedDriver: any): boolean => {
  if (!driver || !selectedDriver || driver.type !== selectedDriver.type) return false;
  if (driver.type === 'indicator') return driver.id === selectedDriver.id;
  if (driver.type === 'dimension') return driver.dim_id === selectedDriver.dim_id;
  return driver.property_id === selectedDriver.property_id;
};

interface DriverCardProperties {
  driver: any;
  isSelected: boolean;
  onSelect: (driver: any) => void;
  textDark: string;
  primaryColor: string;
}

const DriverInputCard = memo(
  ({ driver, isSelected, onSelect, textDark, primaryColor }: DriverCardProperties) => {
    const handleClick = useCallback(() => onSelect(driver), [driver, onSelect]);
    const driverName = getDriverName(driver);

    return (
      <Flex
        as='button'
        type='button'
        onClick={handleClick}
        alignItems='center'
        justifyContent='space-between'
        w='100%'
        p={3}
        gap={2}
        border='1px solid'
        borderColor={isSelected ? primaryColor : PLAN_KPI_SELECTED_BORDER}
        borderRadius={PLAN_REVAMP_RADIUS}
        bg={PLAN_KPI_SELECTED_BG}
        cursor='pointer'
        textAlign='left'
        transition='border-color 0.15s ease'
        _hover={{ borderColor: primaryColor }}
      >
        <Flex minW={0} alignItems='center' gap={1} {...PLAN_TEXT.body} lineHeight='normal'>
          <Text color={textDark} whiteSpace='nowrap'>
            Enter your
          </Text>
          <Text color={PLAN_ACCENT_BLUE} fontWeight={500} noOfLines={1}>
            {driverName}
          </Text>
        </Flex>
        <ChevronRightIcon
          boxSize={4}
          color={textDark}
          flexShrink={0}
          transform={isSelected ? 'rotate(180deg)' : undefined}
        />
      </Flex>
    );
  },
);

DriverInputCard.displayName = 'DriverInputCard';

interface Properties {
  onCollapse: () => void;
  refetchRequest: PlanPageRefetchRequest;
}

const InputsPanel = ({ onCollapse, refetchRequest }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const blockDrivers = useSelector(getBlockDriversData);
  const selectedDriver = useSelector(getSelectedDriver);
  const themeColors = useSelector(getPlanPageThemeColors);

  const onSelectDriver = useCallback(
    (driver: any) => {
      if (isSameDriver(driver, selectedDriver)) {
        refetchPlanPageAfterInputPanelClose(refetchRequest, selectedDriver);
        dispatch(setDriver(null));
        return;
      }

      refetchPlanPageAfterInputPanelClose(refetchRequest, selectedDriver);

      if (selectedDriver?.type === 'indicator') {
        dispatch(setDriver(null));
        dispatch(resetIndicator());
      }
      if (selectedDriver?.type === 'property') {
        dispatch(setDriver(null));
        dispatch(resetDimension());
      }
      if (selectedDriver?.type === 'dimension') {
        dispatch(setDriver(null));
        dispatch(resetTableOrgChart());
        dispatch(resetItem());
      }

      dispatch(setDriver(driver));
    },
    [dispatch, refetchRequest, selectedDriver],
  );

  return (
    <Flex
      direction='column'
      w={PLAN_INPUT_PANEL_WIDTH}
      minW={PLAN_INPUT_PANEL_WIDTH}
      flexShrink={0}
      h='100%'
      bg={themeColors.surface}
      borderRight='1px solid'
      borderColor={themeColors.border}
      overflow='hidden'
    >
      <Box px={5} pt={5} flexShrink={0}>
        <Flex alignItems='center' justifyContent='space-between' gap={2}>
          <Text {...PLAN_TEXT.inputsPanelTitle} color={themeColors.textDark}>
            Inputs
          </Text>
          <IconButton
            aria-label='Collapse inputs panel'
            icon={<ChevronLeftIcon boxSize={5} />}
            variant='ghost'
            size='sm'
            minW='20px'
            w='20px'
            h='20px'
            p={0}
            color={themeColors.textDark}
            _hover={{ bg: 'transparent', color: themeColors.primary }}
            onClick={onCollapse}
          />
        </Flex>
        <Text mt={2.5} {...PLAN_TEXT.body} color={themeColors.textSecondary}>
          Enter and adjust your inputs here
        </Text>
      </Box>

      <Box
        flex='1'
        px={5}
        pt='30px'
        pb={5}
        overflowY='auto'
        className='scroll'
        display='flex'
        flexDirection='column'
        gap={3}
      >
        {(blockDrivers || []).map((driver: any, index: number) => (
          <DriverInputCard
            key={getDriverKey(driver, index)}
            driver={driver}
            isSelected={isSameDriver(driver, selectedDriver)}
            onSelect={onSelectDriver}
            textDark={themeColors.textDark}
            primaryColor={themeColors.primary}
          />
        ))}
      </Box>
    </Flex>
  );
};

export default memo(InputsPanel);
